<!--
Fork addition (Stage G): one-click "Tidy layout" — rearranges all
cards into flow order (scripts/tidy-layout.ts), positions only.
A companion "Undo tidy" button appears after applying and restores
the exact previous positions. The snapshot is single-level session
state held in this component — never written to the save format.
Loading a different plan drops it: BlueprintModelImpl.load() is the
only path that resets itemsGenerationNumber to 0, and the canUndo
check also re-validates every snapshot key against the live board,
so a stale snapshot can never be applied to another plan.
-->
<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {mdiAutoFix, mdiUndo} from '@mdi/js';
import {injectBlueprintModel} from '@/scripts/model/store';
import {computeTidyLayoutForModel} from '@/scripts/tidy-layout';

const blueprintModel = injectBlueprintModel();

const snapshot = ref<Map<string, {x: number; y: number}> | undefined>(undefined);

watch(() => blueprintModel.itemsGenerationNumber, (next, prev) => {
    if(next < prev)
        snapshot.value = undefined;
});

const canUndo = computed(() => {
    const snap = snapshot.value;
    if(!snap || !snap.size)
        return false;
    //re-validate against the live board whenever it changes
    void blueprintModel.itemsGenerationNumber;
    for(const key of snap.keys()) {
        if(!blueprintModel.itemByKey(key))
            return false;
    }
    return true;
});

function tidyLayout() {
    const items = [...blueprintModel.items];
    //empty or single-item board: nothing to rearrange
    if(items.length < 2)
        return;
    const previous = new Map(items.map((item) => [item.key, {x: item.rect.x, y: item.rect.y}]));
    const layout = computeTidyLayoutForModel(blueprintModel);
    for(const item of items) {
        const point = layout.get(item.key);
        if(point)
            item.setRect(item.rect.assign(point));
    }
    //a second tidy replaces the snapshot
    snapshot.value = previous;
    //same notification the upstream auto-layout uses: re-frames the
    //surface so the whole new layout stays inside the virtual canvas
    blueprintModel.layoutChanged();
}

function undoTidy() {
    const snap = snapshot.value;
    if(!snap)
        return;
    for(const [key, point] of snap) {
        const item = blueprintModel.itemByKey(key);
        if(item)
            item.setRect(item.rect.assign(point));
    }
    snapshot.value = undefined;
    blueprintModel.layoutChanged();
}
</script>

<template>
    <v-tooltip text="Rearrange all factories into flow order" location="bottom">
        <template #activator="{props: tooltipProps}">
            <v-btn v-bind="tooltipProps" @click="tidyLayout()">
                <v-icon :icon="mdiAutoFix" start />
                Tidy layout
            </v-btn>
        </template>
    </v-tooltip>
    <tooltip-button
        v-if="canUndo"
        tooltip="Undo tidy"
        :icon="mdiUndo"
        @click="undoTidy()"
    />
</template>
